import React, { useState, useEffect, useCallback } from 'react';
import {
  FaPlaneDeparture, FaPlus, FaCheck, FaTimes, FaTrash, FaHistory,
  FaInbox, FaUserClock,
} from 'react-icons/fa';
import api, { errorMessage, getRole, getUser } from '../api';
import { LEAVE_APPROVERS, hasRole } from './attendance/roles';
import {
  PageShell, Card, Badge, Button, Modal, Field, inputClass, Alert, Loading,
  Table, EmptyState, formatDate, formatDateTime, todayISO,
} from './attendance/ui';

/**
 * Leave requests for everyone: staff file and track their own, approvers
 * review the queue for the staff they oversee.
 */
const LeaveManagement = () => {
  const role = getRole();
  const user = getUser();
  const isApprover = hasRole(role, LEAVE_APPROVERS);

  const [tab, setTab] = useState(isApprover ? 'queue' : 'mine');
  const [mine, setMine] = useState([]);
  const [queue, setQueue] = useState([]);
  const [balances, setBalances] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ leaveType: '', startDate: todayISO(), endDate: todayISO(), reason: '' });
  const [saving, setSaving] = useState(false);

  const [review, setReview] = useState(null);   // { request, decision }
  const [comment, setComment] = useState('');

  const load = useCallback(async () => {
    setError('');
    try {
      const requests = [
        api.get('leave-requests/', { params: { personal: 'true' } }),
        api.get('leave-balances/mine/'),
        api.get('leave-types/', { params: { is_active: true } }),
      ];
      if (isApprover) requests.push(api.get('leave-requests/'));

      const [mineRes, balanceRes, typeRes, allRes] = await Promise.all(requests);
      setMine(mineRes.data);
      setBalances(balanceRes.data);
      setLeaveTypes(typeRes.data);
      if (allRes) setQueue(allRes.data.filter((item) => item.employeeId !== user?.employeeId));
    } catch (err) {
      setError(errorMessage(err, 'Could not load leave information.'));
    } finally {
      setLoading(false);
    }
  }, [isApprover, user?.employeeId]);

  useEffect(() => { load(); }, [load]);

  const submit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      await api.post('leave-requests/', form);
      setFormOpen(false);
      setForm({ leaveType: '', startDate: todayISO(), endDate: todayISO(), reason: '' });
      setNotice('Leave request submitted. Your approver has been notified.');
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not submit the request.'));
    } finally {
      setSaving(false);
    }
  };

  const decide = async () => {
    setSaving(true);
    setError('');
    try {
      await api.post(`leave-requests/${review.request.id}/${review.decision}/`, { comment });
      setNotice(`Request ${review.decision === 'approve' ? 'approved' : 'rejected'}.`);
      setReview(null);
      setComment('');
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not record the decision.'));
    } finally {
      setSaving(false);
    }
  };

  const cancel = async (request) => {
    if (!window.confirm('Cancel this leave request?')) return;
    try {
      await api.post(`leave-requests/${request.id}/cancel/`);
      setNotice('Leave request cancelled.');
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not cancel the request.'));
    }
  };

  const withdraw = async (request) => {
    if (!window.confirm('Withdraw and delete this request?')) return;
    try {
      await api.delete(`leave-requests/${request.id}/`);
      setNotice('Leave request withdrawn.');
      await load();
    } catch (err) {
      setError(errorMessage(err, 'Could not withdraw the request.'));
    }
  };

  const pending = queue.filter((item) => item.status === 'pending');
  const decided = queue.filter((item) => item.status !== 'pending');

  const tabs = [
    ...(isApprover ? [{ key: 'queue', label: `Approvals (${pending.length})`, icon: <FaInbox /> }] : []),
    { key: 'mine', label: 'My requests', icon: <FaUserClock /> },
    ...(isApprover ? [{ key: 'history', label: 'Decision history', icon: <FaHistory /> }] : []),
  ];

  if (loading) return <PageShell title="Leave Management"><Loading /></PageShell>;

  return (
    <PageShell
      title="Leave Management"
      subtitle="Request, track and approve staff leave"
      actions={<Button onClick={() => setFormOpen(true)}><FaPlus /> Request leave</Button>}
    >
      <Alert>{error}</Alert>
      <Alert tone="success">{notice}</Alert>

      {/* Balances */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
        {balances.map((balance) => (
          <div key={balance.id} className="bg-white rounded-2xl shadow p-4">
            <div className="text-xs text-gray-500 truncate" title={balance.leaveType}>{balance.leaveType}</div>
            <div className="text-2xl font-bold text-gray-800 mt-1">{Number(balance.remainingDays)}</div>
            <div className="text-xs text-gray-400">
              of {Number(balance.entitledDays) + Number(balance.carriedForwardDays)} days
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2 mb-5 flex-wrap">
        {tabs.map((item) => (
          <button key={item.key} onClick={() => setTab(item.key)}
            className={`px-4 py-2.5 rounded-xl font-semibold text-sm flex items-center gap-2 transition-all ${tab === item.key ? 'bg-[#15A857] text-white shadow-lg shadow-[#15A857]/20' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
            {item.icon} {item.label}
          </button>
        ))}
      </div>

      {tab === 'queue' && (
        <Card title="Awaiting your decision">
          {pending.length === 0 ? (
            <EmptyState icon={<FaCheck />} title="Nothing to approve"
              hint="Requests from the staff you oversee will appear here." />
          ) : (
            <div className="space-y-4">
              {pending.map((request) => (
                <div key={request.id} className="border border-gray-100 rounded-xl p-4 hover:border-[#15A857]/40 transition-colors">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-semibold text-gray-800">{request.employeeName}</span>
                        <span className="text-xs text-gray-500 font-mono">{request.employeeId}</span>
                        <span className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                          style={{ backgroundColor: request.leaveTypeColor || '#15A857' }}>
                          {request.leaveTypeName}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600">
                        {formatDate(request.startDate)} → {formatDate(request.endDate)}
                        {' · '}<strong>{Number(request.days)} day(s)</strong>
                        {request.department ? ` · ${request.department}` : ''}
                      </div>
                      <p className="text-sm text-gray-500 mt-1.5">{request.reason}</p>
                      <div className="text-xs text-gray-400 mt-1">Filed {formatDateTime(request.createdAt)}</div>
                    </div>
                    <div className="flex gap-2 flex-shrink-0">
                      <Button onClick={() => { setReview({ request, decision: 'approve' }); setComment(''); }}>
                        <FaCheck /> Approve
                      </Button>
                      <Button variant="danger" onClick={() => { setReview({ request, decision: 'reject' }); setComment(''); }}>
                        <FaTimes /> Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {tab === 'mine' && (
        <Card title="My leave requests">
          <Table
            columns={['Type', 'From', 'To', 'Days', 'Reason', 'Status', 'Reviewer', '']}
            rows={mine}
            empty="You have not requested any leave yet."
            renderRow={(request) => (
              <tr key={request.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-3 pr-4 whitespace-nowrap">
                  <span className="px-2 py-0.5 rounded-full text-xs font-semibold text-white"
                    style={{ backgroundColor: request.leaveTypeColor || '#15A857' }}>
                    {request.leaveTypeName}
                  </span>
                </td>
                <td className="py-3 pr-4 whitespace-nowrap">{request.startDate}</td>
                <td className="py-3 pr-4 whitespace-nowrap">{request.endDate}</td>
                <td className="py-3 pr-4">{Number(request.days)}</td>
                <td className="py-3 pr-4 max-w-xs truncate" title={request.reason}>{request.reason}</td>
                <td className="py-3 pr-4"><Badge value={request.status} /></td>
                <td className="py-3 pr-4 text-gray-500">
                  {request.reviewedBy || '—'}
                  {request.reviewComment && (
                    <div className="text-xs text-gray-400 italic">“{request.reviewComment}”</div>
                  )}
                </td>
                <td className="py-3 pr-4 whitespace-nowrap">
                  {request.status === 'pending' && (
                    <button onClick={() => withdraw(request)}
                      className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg" title="Withdraw">
                      <FaTrash />
                    </button>
                  )}
                  {request.status === 'approved' && (
                    <button onClick={() => cancel(request)}
                      className="text-xs font-semibold text-red-500 hover:underline">
                      Cancel
                    </button>
                  )}
                </td>
              </tr>
            )}
          />
        </Card>
      )}

      {tab === 'history' && (
        <Card title="Decided requests">
          <Table
            columns={['Staff', 'Type', 'From', 'To', 'Days', 'Status', 'Reviewed by', 'Reviewed at']}
            rows={decided}
            empty="No decisions recorded yet."
            renderRow={(request) => (
              <tr key={request.id} className="border-b last:border-0 hover:bg-gray-50">
                <td className="py-3 pr-4 font-medium text-gray-800 whitespace-nowrap">{request.employeeName}</td>
                <td className="py-3 pr-4 whitespace-nowrap">{request.leaveTypeName}</td>
                <td className="py-3 pr-4 whitespace-nowrap">{request.startDate}</td>
                <td className="py-3 pr-4 whitespace-nowrap">{request.endDate}</td>
                <td className="py-3 pr-4">{Number(request.days)}</td>
                <td className="py-3 pr-4"><Badge value={request.status} /></td>
                <td className="py-3 pr-4">{request.reviewedBy || '—'}</td>
                <td className="py-3 pr-4 whitespace-nowrap">{formatDateTime(request.reviewedAt)}</td>
              </tr>
            )}
          />
        </Card>
      )}

      {/* New request */}
      <Modal isOpen={formOpen} onClose={() => setFormOpen(false)} title="Request leave" size="max-w-lg">
        <form onSubmit={submit} className="space-y-4">
          <Field label="Leave type">
            <select required value={form.leaveType} className={inputClass}
              onChange={(event) => setForm({ ...form, leaveType: event.target.value })}>
              <option value="">Select a leave type…</option>
              {leaveTypes.map((type) => (
                <option key={type.id} value={type.id}>
                  {type.name}{type.daysAllowedPerYear ? ` (${type.daysAllowedPerYear} days/year)` : ''}
                </option>
              ))}
            </select>
          </Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="From">
              <input type="date" required value={form.startDate} className={inputClass}
                onChange={(event) => setForm({ ...form, startDate: event.target.value })} />
            </Field>
            <Field label="To">
              <input type="date" required value={form.endDate} className={inputClass}
                onChange={(event) => setForm({ ...form, endDate: event.target.value })} />
            </Field>
          </div>
          <Field label="Reason" hint="Weekends and public holidays are not counted against your balance.">
            <textarea required rows="4" value={form.reason} className={inputClass}
              placeholder="Briefly explain the reason for your leave."
              onChange={(event) => setForm({ ...form, reason: event.target.value })} />
          </Field>
          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={saving} className="flex-1">
              <FaPlaneDeparture /> {saving ? 'Submitting…' : 'Submit request'}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setFormOpen(false)}>Cancel</Button>
          </div>
        </form>
      </Modal>

      {/* Approve / reject */}
      <Modal isOpen={!!review} onClose={() => setReview(null)}
        title={review?.decision === 'approve' ? 'Approve leave request' : 'Reject leave request'} size="max-w-lg">
        {review && (
          <div className="space-y-4">
            <div className="bg-gray-50 rounded-xl p-4 text-sm">
              <div className="font-semibold text-gray-800">{review.request.employeeName}</div>
              <div className="text-gray-600 mt-1">
                {review.request.leaveTypeName} · {formatDate(review.request.startDate)} →{' '}
                {formatDate(review.request.endDate)} · {Number(review.request.days)} day(s)
              </div>
              <p className="text-gray-500 mt-2">{review.request.reason}</p>
            </div>
            <Field label="Comment" hint="Shared with the staff member in their notification.">
              <textarea rows="3" value={comment} className={inputClass}
                onChange={(event) => setComment(event.target.value)}
                placeholder={review.decision === 'approve' ? 'Approved.' : 'Reason for rejection…'} />
            </Field>
            <div className="flex gap-3">
              <Button onClick={decide} disabled={saving}
                variant={review.decision === 'approve' ? 'primary' : 'danger'} className="flex-1">
                {review.decision === 'approve' ? <FaCheck /> : <FaTimes />}
                {saving ? 'Saving…' : review.decision === 'approve' ? 'Approve' : 'Reject'}
              </Button>
              <Button variant="secondary" onClick={() => setReview(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </Modal>
    </PageShell>
  );
};

export default LeaveManagement;
